import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { IProduct, IVariableDescription } from '../../../shared/types/Store';
import { useCart } from '../../../shared/contexts/CartContext';
import { useToast } from '../../../shared/contexts/ToastContext';
import { useStatus } from '../../../shared/contexts/StatusContext';
import { useStore } from '../../../shared/contexts/StoreContext';
import { ProductImage } from '../../../shared/components/ProductImage';
import { ProductModal } from '../../../shared/components/ProductModal';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ScrollArrow = styled.button<{ $side: 'left' | 'right' }>`
  display: none;

  @media (min-width: 1024px) {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    ${props => props.$side === 'left' ? 'left: -24px;' : 'right: -24px;'}
    z-index: 5;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 2px solid #000;
    background: rgba(255, 255, 255, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s;
    flex-shrink: 0;

    &:hover {
      background: rgba(255, 255, 255, 1);
    }

    &:active {
      transform: translateY(-50%) scale(0.92);
    }
  }
`;

const Container = styled.div<{ $few: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 1024px) {
    flex-direction: row;
    gap: 20px;

    ${({ $few }) => $few
      ? `
        flex-wrap: wrap;
      `
      : `
        overflow-x: auto;
        padding-bottom: 10px;
        scroll-snap-type: x mandatory;
      `
    }

    &::-webkit-scrollbar { height: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
  }
`;

const Card = styled.div<{ $few: boolean }>`
  background: white;
  border-radius: 1.5rem;
  padding: 16px;
  display: flex;
  gap: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  border: 1px solid #f3f4f6;
  transition: all 0.2s;

  &:hover {
    border-color: #fed7aa;
  }

  @media (min-width: 1024px) {
    flex-direction: column;

    ${({ $few }) => $few
      ? `
        flex: 1;
        min-width: 280px;
        max-width: 420px;
      `
      : `
        min-width: 300px;
        max-width: 340px;
        flex-shrink: 0;
        scroll-snap-align: start;
      `
    }
  }
`;

const ImageWrapper = styled.div`
  flex-shrink: 0;

  @media (min-width: 1024px) {
    margin: -16px -16px 12px;
    width: calc(100% + 32px);
    height: 160px;
    overflow: hidden;
    border-radius: 1.5rem 1.5rem 0 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 0 !important;
      box-shadow: none !important;
    }
  }
`;

const ProductInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ProductName = styled.h3`
  font-size: 16px;
  font-weight: 800;
  color: #1f2937;
  margin-bottom: 4px;
`;

const ProductDesc = styled.p`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
  max-height: 56px;
  overflow-y: auto;
  overflow-x: hidden;
  word-break: break-word;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;

const Price = styled.span`
  font-size: 18px;
  font-weight: 900;
  color: var(--secondary);
`;

const OldPrice = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #9ca3af;
  text-decoration: line-through;
  margin-right: 4px;
`;

const PromoBadge = styled.span`
  font-size: 11px;
  font-weight: 800;
  color: white;
  background: #ef4444;
  padding: 2px 6px;
  border-radius: 6px;
  margin-left: 6px;
  white-space: nowrap;
`;

const ScheduleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-top: 6px;
  width: 100%;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  white-space: normal;
  text-align: center;
  line-height: 1.4;
  transition: background 0.15s;

  &:active {
    background: #e5e7eb;
  }
`;

const AddButton = styled.button<{ $disabled?: boolean }>`
  background: ${props => props.$disabled ? '#9ca3af' : 'var(--primary)'};
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  box-shadow: ${props => props.$disabled ? 'none' : '0 4px 6px rgba(245, 130, 32, 0.2)'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  opacity: ${props => props.$disabled ? '0.4' : '1'};

  &:active {
    transform: ${props => props.$disabled ? 'none' : 'scale(0.9)'};
  }
`;

interface ProductListProps {
    products: IProduct[];
}

export const ProductList: React.FC<ProductListProps> = ({ products }) => {
    const { addItem } = useCart();
    const { showToast } = useToast();
    const { isOpen, isGestorMode } = useStatus();
    const {
        flavors, flavorLists,
        sauces, sauceLists,
        sideDishes, sideDishLists,
        additionals, additionalLists,
        ingredients, ingredientLists,
        variableDescriptions
    } = useStore();

    const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
    const [scheduleProduct, setScheduleProduct] = useState<IProduct | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    const few = products.length <= 2;

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    useEffect(() => {
        if (few) return;
        const el = scrollRef.current;
        if (!el) return;
        updateScrollState();
        el.addEventListener('scroll', updateScrollState);
        const ro = new ResizeObserver(updateScrollState);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', updateScrollState);
            ro.disconnect();
        };
    }, [few, updateScrollState, products]);

    const handleScrollBy = (direction: 'left' | 'right') => {
        const el = scrollRef.current;
        if (!el) return;
        const amount = el.clientWidth * 0.6;
        el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
    };

    const getVariableSideDishes = (product: IProduct): any[] => {
        if (!(product as any).hasVariablePrice || !product.hasSideDish) return [];
        const listId = (product as any).sideDishListId?._id || product.sideDishListId;
        const list = listId ? sideDishLists.find((l: any) => String(l.id || l._id) === String(listId)) : null;
        if (list && Array.isArray(list.sideDishes) && list.sideDishes.length > 0) {
            return list.sideDishes.map((s: any) =>
                typeof s === 'object' && s !== null ? { ...s, id: s.id || s._id } : null
            ).filter(Boolean);
        }
        return (sideDishes as any[]);
    };

    const getVariableMinPrice = (product: IProduct): number | null => {
        if (!(product as any).hasVariablePrice || !product.hasSideDish) return null;
        const items = getVariableSideDishes(product);
        const withPrice = items.filter((s: any) => Number(s?.price) > 0);
        if (withPrice.length > 0) return Math.min(...withPrice.map((s: any) => Number(s.price)));
        return 0;
    };

    const isProductAvailable = (product: IProduct): boolean => {
        if (!(product as any).hasSpecificSchedule) return true;
        const schedule: any[] = (product as any).specificSchedule || [];
        if (schedule.length === 0) return true;
        const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
        const now = new Date();
        const todayName = dayNames[now.getDay()];
        const todaySchedule = schedule.find(d => d.day === todayName);
        if (!todaySchedule?.enabled) return false;
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        return todaySchedule.shifts.some((shift: any) => {
            const [oh, om] = shift.open.split(':').map(Number);
            const [ch, cm] = shift.close.split(':').map(Number);
            return currentMinutes >= oh * 60 + om && currentMinutes <= ch * 60 + cm;
        });
    };

    const getScheduleText = (product: IProduct): string => {
        const schedule: any[] = (product as any).specificSchedule || [];
        const enabled = schedule.filter(d => d.enabled);
        if (enabled.length === 0) return '';
        return enabled.map(d => `${d.day}: ${d.shifts.map((s: any) => `${s.open}–${s.close}`).join(', ')}`).join(' | ');
    };

    const getProductDescription = (product: IProduct) => {
        if (product.useVariableDescription && product.variableDescriptionId) {
            const list = variableDescriptions.find((v: IVariableDescription) => (v._id || v.id) === product.variableDescriptionId);
            if (list) {
                const days = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];
                const today = days[new Date().getDay()];
                // @ts-ignore
                const dayConfig = list.days[today];
                if (dayConfig && dayConfig.enabled && dayConfig.description) {
                    return dayConfig.description;
                }
            }
        }
        if ((product as any).isCombo && (product as any).comboItems?.length > 0 && !product.desc) {
            return 'Inclui: ' + (product as any).comboItems
                .map((ci: any) => ci.quantity > 1 ? `${ci.productName} (×${ci.quantity})` : ci.productName)
                .join(', ');
        }
        return product.desc;
    };

    const handleAddToCart = (e?: React.MouseEvent, product?: IProduct) => {
        if (e) e.stopPropagation();
        const prod = product || selectedProduct;
        if (!prod) return;

        if (!isOpen && !isGestorMode) {
            showToast('Estamos fechados no momento', 'warning');
            return;
        }

        if ((prod as any).hasVariableSizePrice && (prod as any).sizes?.length > 0) {
            const sizes = (prod as any).sizes as any[];
            const cheapest = sizes.reduce((min: any, s: any) => (s.price || 0) < (min.price || 0) ? s : min, sizes[0]);
            if (cheapest) {
                addItem({ ...prod, price: cheapest.price || 0 }, 1, { size: cheapest.name });
                showToast('Produto adicionado!', 'success');
                return;
            }
        }

        if ((prod as any).hasVariablePrice && prod.hasSideDish) {
            const allSides = getVariableSideDishes(prod);
            const cheapest = allSides.length > 0
                ? allSides.reduce((min: any, s: any) => (s.price || 0) < (min.price || 0) ? s : min, allSides[0])
                : null;
            if (cheapest) {
                addItem({ ...prod, price: cheapest.price || 0 }, 1, { sideDishes: [cheapest.name] });
                showToast('Produto adicionado!', 'success');
                return;
            }
        }

        addItem(prod, 1);
        showToast('Produto adicionado!', 'success');
    };

    const getPromoPrice = (product: IProduct) => {
        const promo = (product as any).promotion;
        if (!promo?.active || !promo.value) return null;
        if (promo.expirationDate && new Date(promo.expirationDate) < new Date()) return null;
        if (promo.type === 'percentage') return product.price * (1 - promo.value / 100);
        return Math.max(0, product.price - promo.value);
    };

    const getPromoBadge = (product: IProduct) => {
        const promo = (product as any).promotion;
        if (!promo?.active || !promo.value) return null;
        if (promo.expirationDate && new Date(promo.expirationDate) < new Date()) return null;
        return promo.type === 'percentage'
            ? `${promo.value}% OFF`
            : `R$ ${Number(promo.value).toFixed(2)} OFF`;
    };

    return (
        <>
            <Wrapper>
                {!few && canScrollLeft && (
                    <ScrollArrow $side="left" onClick={() => handleScrollBy('left')}>
                        <i className="fas fa-chevron-left" style={{ fontSize: '14px', color: '#111' }} />
                    </ScrollArrow>
                )}
                <Container $few={few} ref={scrollRef}>
                {products.map((product, index) => {
                    const promoPrice = getPromoPrice(product);
                    const promoBadge = getPromoBadge(product);
                    const varMinPrice = getVariableMinPrice(product);
                    const available = isProductAvailable(product);
                    const varSizeMinPrice = (product as any).hasVariableSizePrice && (product as any).sizes?.length > 0
                        ? Math.min(...((product as any).sizes as any[]).filter((s: any) => (s.price || 0) > 0).map((s: any) => Number(s.price)))
                        : null;
                    return (
                    <Card key={`${product.id}-${index}`} $few={few} style={{ opacity: !available ? 0.75 : 1 }}>
                        {product.image && (
                            <ImageWrapper onClick={() => setSelectedProduct(product)} style={{ cursor: 'zoom-in' }}>
                                <ProductImage
                                    imageId={product.image}
                                    alt={product.name}
                                />
                            </ImageWrapper>
                        )}
                        <ProductInfo onClick={() => setSelectedProduct(product)} style={{ cursor: 'pointer' }}>
                            <div>
                                <ProductName>{product.name}</ProductName>
                                {getProductDescription(product) && <ProductDesc>{getProductDescription(product)}</ProductDesc>}
                                {!available && (
                                    <ScheduleButton onClick={(e) => { e.stopPropagation(); setScheduleProduct(product); }}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                                        </svg>
                                        <span>Produto não disponível no horário<br/>ver horários</span>
                                    </ScheduleButton>
                                )}
                            </div>
                            <PriceRow>
                                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px' }}>
                                    {(product as any).hasVariableSizePrice && varSizeMinPrice !== null ? (
                                        <Price style={{ fontSize: '14px' }}>
                                            a partir de {varSizeMinPrice.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                                        </Price>
                                    ) : (product as any).hasVariablePrice && product.hasSideDish ? (
                                        <Price style={{ fontSize: '14px' }}>
                                            a partir de {(varMinPrice ?? 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                                        </Price>
                                    ) : promoPrice !== null ? (
                                        <>
                                            <OldPrice>{product.price.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}</OldPrice>
                                            <Price>{promoPrice.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}</Price>
                                            {promoBadge && <PromoBadge>{promoBadge}</PromoBadge>}
                                        </>
                                    ) : (
                                        <Price>{product.price.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}</Price>
                                    )}
                                </div>
                                <AddButton
                                    onClick={(e) => { e.stopPropagation(); handleAddToCart(e, product); }}
                                    $disabled={(!isOpen && !isGestorMode) || !available}
                                >
                                    <i className="fas fa-plus"></i>
                                </AddButton>
                            </PriceRow>
                        </ProductInfo>
                    </Card>
                    );
                })}
                </Container>
                {!few && canScrollRight && (
                    <ScrollArrow $side="right" onClick={() => handleScrollBy('right')}>
                        <i className="fas fa-chevron-right" style={{ fontSize: '14px', color: '#111' }} />
                    </ScrollArrow>
                )}
            </Wrapper>

            <ProductModal
                product={selectedProduct}
                isOpen={!!selectedProduct}
                onClose={() => setSelectedProduct(null)}
                flavorOptions={flavors}
                flavorLists={flavorLists}
                sauceOptions={sauces}
                sauceLists={sauceLists}
                sideDishOptions={sideDishes}
                sideDishLists={sideDishLists}
                additionalOptions={additionals}
                additionalLists={additionalLists}
                ingredientOptions={ingredients}
                ingredientLists={ingredientLists}
            />

            {scheduleProduct && (
                <div
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
                    onClick={() => setScheduleProduct(null)}
                >
                    <div
                        style={{ background: 'white', borderRadius: '16px', padding: '24px', maxWidth: '340px', width: '100%' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                            </svg>
                            <span style={{ fontSize: '1rem', fontWeight: 800, color: '#1f2937' }}>Horários disponíveis</span>
                        </div>
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '14px', lineHeight: 1.5 }}>
                            {scheduleProduct.name} está disponível apenas nos seguintes horários:
                        </p>
                        <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '12px', marginBottom: '20px' }}>
                            {getScheduleText(scheduleProduct).split(' | ').map((line, i) => (
                                <div key={i} style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 600, lineHeight: 1.9 }}>{line}</div>
                            ))}
                        </div>
                        <button
                            onClick={() => setScheduleProduct(null)}
                            style={{ width: '100%', padding: '12px', borderRadius: '10px', background: '#e5e7eb', color: '#374151', border: 'none', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};
